import {
  Box,
  Container,
  Divider,
  Grid,
  Typography,
  Paper,
  LinearProgress,
  Stack,
  Chip,
} from "@mui/material";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import TextEditorViewer from "../../../../components/display/TextEditorViewer";
import VaccinationTable from "./VaccinationTable";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import { useGetFinishAppointment } from "../hooks/AppointmentDetails";
import useAppointmentGeneral from "../hooks/useAppointmentGeneral";
import SingleBiometric from "../display/SingleBiometric";
import { fileControlField } from "../../../../components/form/FileUploadBody";
import FilesTable from "./FilesTable";
import { useIsMobile } from "../../../../utils/Functions";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeartOutlined";
import VaccinesIcon from "@mui/icons-material/VaccinesOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFileOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import usePrimaryColor from "../../../../hooks/usePrimaryColor";
import { formatCurrency } from "../../../admin/clinic/components/warehouse/utils/stockStatus";
import { useGetWarehousePreferences } from "../../../admin/clinic/components/warehouse/hooks/useGetWarehousePreferences";
import useUser from "../../../../hooks/useUser";

function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      {icon}
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
  );
}

export function prepareResourcesForPayload(items) {
  return items?.map((item) => {
    return {
      id: item.id,
      quantity: item.quantity,
    };
  });
}

function ResourcesSummary() {
  const { t } = useTranslation();
  const color = usePrimaryColor();
  const { watch } = useFormContext();
  const { currentClinic } = useUser();
  const { data: warehousePreferences } = useGetWarehousePreferences(currentClinic?.id);
  const currency = warehousePreferences?.currency ?? "EUR";
  const selectedItems = watch("selectedItems") ?? new Map();
  const items = Array.from(selectedItems.values());
  if (items.length === 0) return null;

  const total = items.reduce(
    (sum, item) =>
      sum +
      (item.discountedPriceWithTax ?? item.priceWithTax ?? 0) * item.quantity,
    0,
  );

  return (
    <Box mb={2} data-testid="appointment.finish-meeting.overview.resources-section">
      <Paper elevation={2} sx={{ padding: 2 }}>
        <SectionHeader
          icon={<InventoryIcon color="action" />}
          title={t("Resources")}
        />
        <Divider sx={{ mb: 2 }} />

        <Stack divider={<Divider />}>
          {items.map((item) => {
            const unitPrice =
              item.discountedPriceWithTax ?? item.priceWithTax ?? 0;
            const lineTotal = unitPrice * item.quantity;
            return (
              <Stack
                key={item.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ py: 1.25 }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    noWrap
                    title={item.title}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(unitPrice, currency)} {t("PerUnit")}
                  </Typography>
                </Box>

                <Chip
                  label={`x${item.quantity}`}
                  size="small"
                  sx={{ fontWeight: 600, flexShrink: 0 }}
                />

                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ minWidth: 72, textAlign: "right", flexShrink: 0 }}
                >
                  {formatCurrency(lineTotal, currency)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1" fontWeight={600}>
            {t("TotalPrice")}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color={color}>
            {formatCurrency(total, currency)}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

export default function OverView() {
  const { data, isLoading, error } = useAppointmentGeneral();
  const appointment = useGetFinishAppointment(data?.appointment);
  const { t } = useTranslation();
  const methods = useFormContext();
  const vaccinations = methods.getValues("vaccinations");
  const description = methods.getValues("description");
  const biometrics = methods.getValues("biometrics");
  const files = methods.watch(fileControlField);
  const isMobile = useIsMobile();

  if (isLoading) {
    return <LinearProgress />;
  } else if (error) {
    return (
      <SimpleAlert
        message={"ErrorFetchingAppointmentData"}
        severity={"error"}
      />
    );
  }

  return (
    <Container disableGutters={isMobile}>
      {/* Appointment Overview Section */}
      <Box mb={2} data-testid="appointment.finish-meeting.overview.details-section">
        <Paper elevation={2} sx={{ padding: 2 }}>
          <SectionHeader
            icon={<InfoOutlinedIcon color="action" />}
            title={t("AppointmentDetails")}
          />
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3} sx={{ p: 2 }}>
            {appointment?.map((item) => {
              if (item?.label == "Description") {
                return (
                  <Grid item xs={12} key={item?.label} overflow={"auto"}>
                    <Typography variant="body2" color="textSecondary">
                      {t(item?.label)}
                    </Typography>
                    <Typography variant="caption">{t(item?.value)}</Typography>
                  </Grid>
                );
              }
              if (item?.value !== "NoInfo") {
                return (
                  <Grid item xs={12} md={6} key={item?.label}>
                    <Typography variant="body2" color="textSecondary">
                      {t(item?.label)}
                    </Typography>
                    <Typography variant="body1">{t(item?.value)}</Typography>
                  </Grid>
                );
              }
              return null;
            })}
          </Grid>
        </Paper>
      </Box>

      {/* Resources Section */}
      <ResourcesSummary />

      {/* Vaccinations Section */}
      {vaccinations && vaccinations.length > 0 && (
        <Box mb={2} data-testid="appointment.finish-meeting.overview.vaccinations-section">
          <VaccinationTable vaccinations={vaccinations} />
        </Box>
      )}
      {files && files.length > 0 && (
        <Box mb={2} data-testid="appointment.finish-meeting.overview.files-section">
          <FilesTable files={files} />
        </Box>
      )}
      {/* Description Section */}
      {description && (
        <Box mb={2} data-testid="appointment.finish-meeting.overview.description-section">
          <Paper elevation={2} sx={{ padding: 2 }}>
            <SectionHeader
              icon={<DescriptionIcon color="action" />}
              title={t("DoctorsLetter")}
            />
            <Divider sx={{ mb: 2 }} />
            <TextEditorViewer
              value={description}
              key="description-on-overview"
            />
          </Paper>
        </Box>
      )}
      {biometrics ? (
        <Box mb={2} data-testid="appointment.finish-meeting.overview.biometrics-section">
          <Paper elevation={2} sx={{ padding: 2 }}>
            <SectionHeader
              icon={<MonitorHeartIcon color="action" />}
              title={t("Biometrics")}
            />
            <Divider sx={{ mb: 2 }} />
            <SingleBiometric biometric={biometrics} paper={false} />
          </Paper>
        </Box>
      ) : null}
    </Container>
  );
}
